import {useEffect, useMemo, useState} from "react";
import {Plus, ChevronRight, Flag, Send, MessageCircle, ArrowBigUp, ArrowBigDown} from "lucide-react";
import {LIFESTYLE_TOPICS} from "@/lib/constants";
import {useSite, Rich} from "@/lib/site";
import {apiGet, apiPost} from "@/lib/api";
import {FormModal} from "@/components/UI";

function Votes({kind, item, votes, setVotes, onScore, notify}) {
  const id = kind === "question" ? item.question_id : item.answer_id;
  const mine = votes[`${kind}:${id}`] || 0;
  const cast = async (v) => {
    const value = mine === v ? 0 : v;
    try {
      const r = await apiPost(`/${kind === "question" ? "questions" : "answers"}/${id}/vote`, {value});
      setVotes(prev => ({...prev, [`${kind}:${id}`]: value})); onScore(r.score);
    } catch { notify("Please try again"); }
  };
  return (
    <div className="vote-col" data-testid={`votes-${id}`}>
      <button data-testid={`upvote-${id}`} className={mine === 1 ? "up on" : "up"} onClick={() => cast(1)} aria-label="Upvote"><ArrowBigUp size={20} fill={mine === 1 ? "currentColor" : "none"}/></button>
      <b data-testid={`score-${id}`}>{item.score || 0}</b>
      <button data-testid={`downvote-${id}`} className={mine === -1 ? "down on" : "down"} onClick={() => cast(-1)} aria-label="Downvote"><ArrowBigDown size={20} fill={mine === -1 ? "currentColor" : "none"}/></button>
    </div>
  );
}

export default function Ask({notify, requireAuth, user, openMember, focus}) {
  const t = useSite();
  const [topic, setTopic] = useState("All");
  const [tag, setTag] = useState("");
  const [questions, setQuestions] = useState([]);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [answerText, setAnswerText] = useState("");
  const [compose, setCompose] = useState(null);
  const [sort, setSort] = useState("top");
  const [votes, setVotes] = useState({});
  useEffect(() => { apiGet("/votes/mine").then(setVotes).catch(() => {}); }, []);
  const sorted = useMemo(() => [...questions].sort((a, b) => sort === "top" ? (b.score || 0) - (a.score || 0) || b.created_at.localeCompare(a.created_at) : sort === "unanswered" ? (a.answers || 0) - (b.answers || 0) : b.created_at.localeCompare(a.created_at)), [questions, sort]);
  const setQScore = (id, score) => { setQuestions(qs => qs.map(q => q.question_id === id ? {...q, score} : q)); setActive(a => a && a.question_id === id ? {...a, score} : a); };

  const topics = useMemo(() => ["All", ...LIFESTYLE_TOPICS], []);

  useEffect(() => {
    apiGet("/questions", {topic: topic === "All" ? "" : topic, tag}).then(qs => {
      setQuestions(qs);
      if (focus) setActive(a => a || qs.find(q => q.question_id === focus) || null);
    }).catch(() => setQuestions([]));
  }, [topic, tag]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active) return;
    apiGet(`/questions/${active.question_id}/answers`).then(setAnswers).catch(() => setAnswers([]));
  }, [active]);

  const follow = (q) => requireAuth(async () => {
    try { await apiPost(`/questions/${q.question_id}/follow`, {}); notify("You are now following this question"); }
    catch { notify("Please try again"); }
  });

  const submitAnswer = (e) => {
    e.preventDefault();
    if (!answerText || !active) return;
    requireAuth(async () => {
      try {
        const r = await apiPost(`/questions/${active.question_id}/answers`, {text: answerText});
        setAnswers(a => [r, ...a]);
        setAnswerText("");
        notify("Answer posted");
      } catch { notify("Please try again"); }
    });
  };

  return (
    <div className="two-pane" data-testid="ask-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">ASK MY COMMUNITY · PUCHHO</div>
          <h1><Rich text={t("ask_title")}/></h1>
          <p>{t("ask_sub")}</p>
        </div>
        <button data-testid="ask-question-button" className="coral-button" onClick={() => requireAuth(() => setCompose({topic: topic === "All" ? "Food & Dining" : topic, text: "", body: ""}))}>
          <Plus size={16}/> Ask a question
        </button>
      </div>

      <div className="two-pane-grid">
        <aside className="topic-rail" data-testid="ask-topic-rail">
          <div className="eyebrow">LIFESTYLE SUBJECTS</div>
          {topics.map(t => (
            <button key={t} data-testid={`ask-topic-${t.replace(/\s+/g,"-").toLowerCase()}`} className={topic === t ? "topic-row active" : "topic-row"} onClick={() => { setTopic(t); setActive(null); }}>
              <span>{t}</span><ChevronRight size={13}/>
            </button>
          ))}
        </aside>

        <section className="topic-main">
          {!active ? (
            <>
              <div className="results-toolbar">
                <strong data-testid="ask-count">{questions.length} questions in {topic}</strong>
                <div className="ask-sort" data-testid="ask-sort">
                  {[["top", "Top"], ["new", "New"], ["unanswered", "Unanswered"]].map(([id, l]) => <button key={id} data-testid={`ask-sort-${id}`} className={sort === id ? "active" : ""} onClick={() => setSort(id)}>{l}</button>)}
                </div>
                {tag && <button data-testid="ask-clear-tag" className="tag-chip active" onClick={() => setTag("")}>#{tag} ✕</button>}
              </div>
              {questions.length === 0 ? (
                <div className="empty-pane">
                  <MessageCircle size={28}/>
                  <h3>No questions in {topic} yet</h3>
                  <p>Be the first to ask — our community will chime in.</p>
                  <button data-testid="empty-ask-cta" className="coral-button" onClick={() => requireAuth(() => setCompose({topic: topic === "All" ? "Food & Dining" : topic, text: "", body: ""}))}>
                    <Plus size={15}/> Ask a question
                  </button>
                </div>
              ) : sorted.map(q => (
                <article key={q.question_id} className="question-row voted" data-testid={`question-${q.question_id}`}>
                  <Votes kind="question" item={q} votes={votes} setVotes={setVotes} notify={notify} onScore={(s) => setQScore(q.question_id, s)}/>
                  <div className="qr-main">
                  <div className="question-top">
                    <span className="avatar small">{q.author?.[0] || "M"}</span>
                    <span><button className="author-link" data-testid={`question-author-${q.question_id}`} onClick={() => q.user_id && openMember(q.user_id)}>{q.author}</button> · {q.topic}</span>
                    <button data-testid="question-report" className="flag" onClick={() => { apiPost("/reports", {content_type: "question", content_id: q.question_id, reason: "Inappropriate"}).then(() => notify("Thanks — report received")).catch(() => notify("Sign in to report")); }}><Flag size={15}/></button>
                  </div>
                  <h3 onClick={() => setActive(q)} style={{cursor:"pointer"}} data-testid={`question-open-${q.question_id}`}>{q.text}</h3>
                  {q.body && <p>{q.body}</p>}
                  <TagList tags={q.tags} onPick={setTag}/>
                  <div className="question-foot">
                    <button data-testid={`question-follow-${q.question_id}`} onClick={() => follow(q)}>Follow question</button>
                    <button data-testid={`question-answers-${q.question_id}`} onClick={() => setActive(q)}><MessageCircle size={13}/> {q.answers || 0} answers</button>
                    <span>{q.followers || 1} following</span>
                  </div>
                  </div>
                </article>
              ))}
            </>
          ) : (
            <div className="qa-detail">
              <button data-testid="question-back" className="back-btn" onClick={() => setActive(null)}>← Back to {topic}</button>
              <div className="qa-head">
                <Votes kind="question" item={active} votes={votes} setVotes={setVotes} notify={notify} onScore={(s) => setQScore(active.question_id, s)}/>
                <div><div className="eyebrow">{active.topic} · asked by {active.author}</div><h2>{active.text}</h2></div>
              </div>
              {active.body && <p className="qa-body">{active.body}</p>}
              <TagList tags={active.tags} onPick={(t) => { setTag(t); setActive(null); }}/>
              <div className="question-foot">
                <button data-testid="detail-follow" onClick={() => follow(active)}>Follow question</button>
                <span>{answers.length} answers</span>
              </div>
              <form className="answer-form" onSubmit={submitAnswer}>
                <textarea data-testid="answer-text" value={answerText} onChange={e => setAnswerText(e.target.value)} placeholder="Share what you know..."/>
                <button data-testid="answer-submit" className="coral-button" type="submit">
                  <Send size={14}/> Post answer
                </button>
              </form>
              <div className="answer-list">
                {[...answers].sort((x, y) => (y.score || 0) - (x.score || 0)).map(a => (
                  <div key={a.answer_id} className="answer-item voted" data-testid={`answer-${a.answer_id}`}>
                    <Votes kind="answer" item={a} votes={votes} setVotes={setVotes} notify={notify} onScore={(s) => setAnswers(as => as.map(x => x.answer_id === a.answer_id ? {...x, score: s} : x))}/>
                    <div><button className="author-link" data-testid={`answer-author-${a.answer_id}`} onClick={() => a.user_id && openMember(a.user_id)}>{a.author}</button>
                    <p>{a.text}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {compose && <QuestionCompose compose={compose} setCompose={setCompose} notify={notify} onPosted={(q) => { setQuestions(prev => [q, ...prev]); setCompose(null); }}/>}
    </div>
  );
}

function TagList({tags, onPick}) {
  if (!tags?.length) return null;
  return (
    <div className="tag-list">
      {tags.map(t => <button key={t} data-testid={`tag-${t}`} className="tag-chip" onClick={() => onPick(t)}>#{t}</button>)}
    </div>
  );
}

function TagInput({tags, setTags}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (t && !tags.includes(t) && tags.length < 6) setTags([...tags, t]);
    setDraft("");
  };
  return (
    <div className="tag-input" data-testid="compose-tags">
      {tags.map(t => <button type="button" key={t} className="tag-chip active" data-testid={`compose-tag-${t}`} onClick={() => setTags(tags.filter(x => x !== t))}>#{t} ✕</button>)}
      <input data-testid="compose-tag-input" value={draft} onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add}
        placeholder={tags.length ? "Add another" : "e.g. navratri, toronto (press Enter)"}/>
    </div>
  );
}

function QuestionCompose({compose, setCompose, notify, onPosted}) {
  const [v, setV] = useState({tags: [], ...compose});
  const submit = async (e) => {
    e.preventDefault();
    if (!v.tags.length) { notify("Add at least one tag so the right people find your question"); return; }
    try {
      const r = await apiPost("/questions", v);
      onPosted(r);
      notify("Question posted");
    } catch { notify("Please try again"); }
  };
  return (
    <FormModal title="Ask the community" eyebrow="NEW QUESTION" close={() => setCompose(null)} testId="ask-compose">
      <form onSubmit={submit}>
        <label>Lifestyle subject
          <select data-testid="compose-topic" value={v.topic} onChange={e => setV({...v, topic: e.target.value})}>
            {LIFESTYLE_TOPICS.map(t => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label>Your question
          <input data-testid="compose-title" required value={v.text} onChange={e => setV({...v, text: e.target.value})} placeholder="Make it specific and clear"/>
        </label>
        <label>Tags (required, up to 6)
          <TagInput tags={v.tags} setTags={(tags) => setV({...v, tags})}/>
        </label>
        <label>Add context (optional)
          <textarea data-testid="compose-body" value={v.body} onChange={e => setV({...v, body: e.target.value})} placeholder="Share any background that helps people answer"/>
        </label>
        <button data-testid="compose-submit" className="coral-button full" type="submit">Post question</button>
      </form>
    </FormModal>
  );
}
